import { useState } from 'react';
import { 
  Building2, Truck, Store, Users, Wrench,
  Smartphone, MessageSquare, Shield, Gift, CreditCard,
  Settings, Brain, ArrowRight, Layers,
  Wallet, BarChart3, Globe, Zap, Lock, 
  Award, MapPin, Bell, FileText, Target,
  TrendingUp, Eye, AlertTriangle, Lightbulb
} from 'lucide-react';

var TAB_IDS = ['ecosystem', 'engagement', 'rewards', 'platform', 'brain'];
var TAB_LABELS = ['B2B Channel Ecosystem', 'User Engagement', 'Rewards & Payout', 'Platform Core', 'AI Brain'];
var TAB_NUMS = ['01', '02', '03', '04', '05'];
var TAB_COLORS = ['#2C39B8', '#7CC326', '#6FC8F5', '#5EA31C', '#2C39B8'];

function TabButton({ id, label, num, color, isActive, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={"arch-tab-" + id}
      className={"flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all " + (isActive ? "text-white shadow-lg" : "bg-gray-100 text-gray-600 hover:bg-gray-200")}
      style={isActive ? { backgroundColor: color } : {}}
    >
      <span className="hidden sm:inline">{label}</span>
      <span className="sm:hidden">Band {num}</span>
    </button>
  );
}

function LayerRow({ icon, name, desc, tag, color }) {
  var Icon = icon;
  return (
    <div className="flex items-center gap-4 p-3 rounded-xl bg-white border border-gray-100 hover:border-blue-200 hover:shadow-sm transition-all">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color + '15' }}>
        <Icon size={18} style={{ color: color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-gray-900 text-sm">{name}</div>
        <div className="text-xs text-gray-500">{desc}</div>
      </div>
      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: color + '15', color: color }}>{tag}</span>
    </div>
  );
}

function FeatureItem({ text, color }) {
  return (
    <div className="flex items-center gap-2 text-xs text-gray-600">
      <div className="w-1 h-1 rounded-full flex-shrink-0" style={{ backgroundColor: color }}></div>
      {text}
    </div>
  );
}

function ModuleCard({ icon, name, desc, color }) {
  var Icon = icon;
  return (
    <div className="p-3 rounded-xl bg-white border border-gray-100 hover:shadow-sm transition-all">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2" style={{ backgroundColor: color + '15' }}>
        <Icon size={16} style={{ color: color }} />
      </div>
      <div className="font-semibold text-gray-900 text-xs">{name}</div>
      <div className="text-[11px] text-gray-500 mt-0.5">{desc}</div>
    </div>
  );
}

function ChatExample({ text }) {
  return (
    <div className="flex items-center gap-2 text-xs text-gray-300 bg-gray-800 rounded-lg px-3 py-2">
      <span className="text-[#7CC326] flex-shrink-0">&#x276F;</span>
      <span className="italic">{text}</span>
    </div>
  );
}

function EcosystemContent() {
  var c = '#3b82f6';
  return (
    <div className="space-y-2">
      <LayerRow icon={Building2} name="Manufacturers / Brand HQ" desc="Program owners & budget controllers" tag="Primary" color={c} />
      <LayerRow icon={Truck} name="Distributors & Super-Stockists" desc="Primary sales — volume-based incentives" tag="Primary Sales" color={c} />
      <LayerRow icon={Store} name="Dealers & Wholesalers" desc="Secondary sales — target & milestone rewards" tag="Secondary Sales" color={c} />
      <LayerRow icon={Store} name="Retailers" desc="Tertiary sales — frequency & display rewards" tag="Tertiary Sales" color={c} />
      <LayerRow icon={Wrench} name="Influencers & Applicators" desc="Painters, plumbers, electricians — recommendation rewards" tag="Point of Sale" color={c} />
      <div className="mt-3 p-3 rounded-lg border border-dashed border-blue-300 bg-blue-50/50">
        <p className="text-xs text-[#2C39B8] font-medium flex items-center gap-1.5">
          <ArrowRight size={12} />
          Each layer connects to the ChannelLoyalty Mobile App + WhatsApp for engagement
        </p>
      </div>
    </div>
  );
}

function EngagementContent() {
  var c = '#7CC326';
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="p-4 rounded-xl bg-white border border-gray-100">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: c + '15' }}>
            <Smartphone size={16} style={{ color: c }} />
          </div>
          <span className="font-semibold text-gray-900 text-sm">ChannelLoyalty Mobile App</span>
        </div>
        <div className="space-y-1.5">
          <FeatureItem text="Enroll & Profile Management" color={c} />
          <FeatureItem text="Points & Milestones Tracking" color={c} />
          <FeatureItem text="Invoice Upload & Scan" color={c} />
          <FeatureItem text="Leaderboards & Badges" color={c} />
          <FeatureItem text="Scheme Browser & Offers" color={c} />
          <FeatureItem text="Instant Reward Redemption" color={c} />
        </div>
      </div>
      <div className="p-4 rounded-xl bg-white border border-gray-100">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: c + '15' }}>
            <MessageSquare size={16} style={{ color: c }} />
          </div>
          <span className="font-semibold text-gray-900 text-sm">WhatsApp Engagement</span>
        </div>
        <div className="space-y-1.5">
          <FeatureItem text="Quick Registration via Link" color={c} />
          <FeatureItem text="Point Balance Check" color={c} />
          <FeatureItem text="Campaign Nudges & Alerts" color={c} />
          <FeatureItem text="Reward Redemption MIS" color={c} />
          <FeatureItem text="Personalised Offers" color={c} />
          <FeatureItem text="Multi-Language Support" color={c} />
        </div>
      </div>
    </div>
  );
}

function RewardsContent() {
  var c = '#10b981';
  return (
    <div className="grid md:grid-cols-3 gap-4">
      <div className="p-4 rounded-xl bg-white border border-gray-100">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: c + '15' }}>
            <Shield size={16} style={{ color: c }} />
          </div>
          <span className="font-semibold text-gray-900 text-sm">KYC & Compliance</span>
        </div>
        <div className="space-y-1.5">
          <FeatureItem text="Mobile OTP Verification" color={c} />
          <FeatureItem text="PAN / Aadhaar Verification" color={c} />
          <FeatureItem text="Duplicate User Detection" color={c} />
          <FeatureItem text="Tiered KYC (Basic vs Full)" color={c} />
        </div>
      </div>
      <div className="p-4 rounded-xl bg-white border border-gray-100">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: c + '15' }}>
            <Gift size={16} style={{ color: c }} />
          </div>
          <span className="font-semibold text-gray-900 text-sm">Reward Catalog</span>
        </div>
        <div className="space-y-1.5">
          <FeatureItem text="UPI / Instant Cash Payouts" color={c} />
          <FeatureItem text="Gift Vouchers & Coupons" color={c} />
          <FeatureItem text="Travel & Holiday Packages" color={c} />
          <FeatureItem text="Insurance & Financial Products" color={c} />
          <FeatureItem text="Education & Skill Credits" color={c} />
          <a href="https://loyltykart.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-[#2C39B8] font-medium hover:underline" data-testid="arch-physical-goods-link">
            <div className="w-1 h-1 rounded-full flex-shrink-0" style={{ backgroundColor: c }}></div>
            Physical Goods &amp; Electronics ↗
          </a>
        </div>
      </div>
      <div className="p-4 rounded-xl bg-white border border-gray-100">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: c + '15' }}>
            <CreditCard size={16} style={{ color: c }} />
          </div>
          <span className="font-semibold text-gray-900 text-sm">Payout Rails</span>
        </div>
        <div className="space-y-1.5">
          <FeatureItem text="UPI Payout Rail (instant)" color={c} />
          <FeatureItem text="Voucher Issuance (tracked)" color={c} />
          <FeatureItem text="Courier / Fulfillment Rail" color={c} />
          <FeatureItem text="Travel Booking Rail" color={c} />
          <FeatureItem text="Insurance Issuance Rail" color={c} />
        </div>
      </div>
    </div>
  );
}

function PlatformContent() {
  var c = '#f59e0b';
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      <ModuleCard icon={Target} name="Program Builder (DIY)" desc="Design loyalty programs visually" color={c} />
      <ModuleCard icon={Users} name="Segmentation & Targeting" desc="Slice channel by any dimension" color={c} />
      <ModuleCard icon={Settings} name="Scheme Rules Engine" desc="Flexible if-then reward logic" color={c} />
      <ModuleCard icon={Wallet} name="Points Ledger & Wallet" desc="Real-time balance management" color={c} />
      <ModuleCard icon={FileText} name="Claim & Invoice Validation" desc="Auto-verify submissions" color={c} />
      <ModuleCard icon={Lock} name="Budget Control & Burn Limits" desc="Prevent overspend in real-time" color={c} />
      <ModuleCard icon={Users} name="User Hierarchy & Roles" desc="Multi-tier access control" color={c} />
      <ModuleCard icon={Bell} name="Notifications & Journeys" desc="Automated engagement flows" color={c} />
      <ModuleCard icon={Globe} name="Integrations (ERP/DMS)" desc="Connect with distributor systems" color={c} />
    </div>
  );
}

function BrainContent() {
  var c = '#ec4899';
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <ModuleCard icon={TrendingUp} name="Sales Productivity Analytics" desc="Primary to Tertiary performance" color={c} />
        <ModuleCard icon={Eye} name="Channel Engagement Scoring" desc="Identify ghost & dormant partners" color={c} />
        <ModuleCard icon={BarChart3} name="Scheme ROI & Cost per Uplift" desc="Measure every rupee spent" color={c} />
        <ModuleCard icon={Award} name="Top Performer Detection" desc="Dealers & influencers driving growth" color={c} />
        <ModuleCard icon={AlertTriangle} name="Fraud & Anomaly Alerts" desc="Real-time pattern detection" color={c} />
        <ModuleCard icon={Lightbulb} name="Predictive Churn Modeling" desc="Forecast who will disengage" color={c} />
      </div>
      <div className="p-4 rounded-xl bg-gray-900 text-white">
        <div className="flex items-center gap-2 mb-3">
          <Brain size={16} className="text-[#7CC326]" />
          <span className="text-xs font-semibold text-[#7CC326] uppercase tracking-wider">Ask the Data — Chat UI</span>
        </div>
        <div className="space-y-2">
          <ChatExample text="Which dealers showed 3x growth this quarter?" />
          <ChatExample text="What is the ROI of our cement loyalty scheme?" />
          <ChatExample text="Show top 5 influencers by secondary sales impact" />
        </div>
      </div>
    </div>
  );
}

var CONTENT_COMPONENTS = {
  ecosystem: EcosystemContent,
  engagement: EngagementContent,
  rewards: RewardsContent,
  platform: PlatformContent,
  brain: BrainContent,
};

var TAB_SUBTITLES = ['Layer-by-Layer Architecture', 'Mobile App + WhatsApp', 'Critical Infrastructure', 'ChannelLoyalty Platform Core', 'GPT AI Insights Layer'];
var TAB_DESCRIPTIONS = [
  'Different layers need different reward logic & engagement frequency. ChannelLoyalty maps the complete distribution hierarchy.',
  'Dual-channel engagement through a dedicated mobile app and conversational WhatsApp loyalty.',
  'End-to-end reward fulfillment with built-in KYC compliance and multiple payout rails.',
  'The central nervous system — DIY program builder, rules engine, budget controls, and full operational management.',
  'AI-powered analytics, conversational insights, predictive churn detection, and ROI analysis.',
];
var TAB_BG = ['bg-blue-50', 'bg-blue-50', 'bg-emerald-50', 'bg-green-50', 'bg-green-50'];
var TAB_BORDER = ['border-blue-200', 'border-blue-200', 'border-emerald-200', 'border-green-200', 'border-green-200'];
var TAB_ICONS = [Building2, Smartphone, Gift, Settings, Brain];

function FlowDot({ color }) {
  return <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: color }}></span>;
}

export default function PlatformArchitecture() {
  var activeIdx = useState(0);
  var idx = activeIdx[0];
  var setIdx = activeIdx[1];

  var ContentComp = CONTENT_COMPONENTS[TAB_IDS[idx]];
  var ActiveIcon = TAB_ICONS[idx];

  return (
    <div className="my-12" data-testid="platform-architecture">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gray-900 flex items-center justify-center">
            <Layers size={20} className="text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Platform Architecture</h2>
            <p className="text-xs text-gray-500">End-to-end B2B Channel Loyalty + Rewards + AI Analytics</p>
          </div>
        </div>
      </div>

      <div className="flex gap-1.5 mb-6 overflow-x-auto pb-2">
        <TabButton id={TAB_IDS[0]} label={TAB_LABELS[0]} num={TAB_NUMS[0]} color={TAB_COLORS[0]} isActive={idx === 0} onClick={function() { setIdx(0); }} />
        <TabButton id={TAB_IDS[1]} label={TAB_LABELS[1]} num={TAB_NUMS[1]} color={TAB_COLORS[1]} isActive={idx === 1} onClick={function() { setIdx(1); }} />
        <TabButton id={TAB_IDS[2]} label={TAB_LABELS[2]} num={TAB_NUMS[2]} color={TAB_COLORS[2]} isActive={idx === 2} onClick={function() { setIdx(2); }} />
        <TabButton id={TAB_IDS[3]} label={TAB_LABELS[3]} num={TAB_NUMS[3]} color={TAB_COLORS[3]} isActive={idx === 3} onClick={function() { setIdx(3); }} />
        <TabButton id={TAB_IDS[4]} label={TAB_LABELS[4]} num={TAB_NUMS[4]} color={TAB_COLORS[4]} isActive={idx === 4} onClick={function() { setIdx(4); }} />
      </div>

      <div className={"rounded-2xl border p-6 transition-all " + TAB_BG[idx] + " " + TAB_BORDER[idx]} data-testid={"arch-content-" + TAB_IDS[idx]}>
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: TAB_COLORS[idx] }}>Band {TAB_NUMS[idx]}</span>
              <span className="text-[10px] text-gray-400">|</span>
              <span className="text-[10px] font-medium text-gray-500">{TAB_SUBTITLES[idx]}</span>
            </div>
            <h3 className="text-lg font-bold text-gray-900">{TAB_LABELS[idx]}</h3>
            <p className="text-sm text-gray-600 mt-1 max-w-xl">{TAB_DESCRIPTIONS[idx]}</p>
          </div>
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: TAB_COLORS[idx] + '15' }}>
            <ActiveIcon size={24} style={{ color: TAB_COLORS[idx] }} />
          </div>
        </div>
        <ContentComp />
      </div>

      <div className="mt-4 flex items-center justify-center gap-3 text-[11px] text-gray-400">
        <FlowDot color={TAB_COLORS[0]} />
        <span>01</span>
        <ArrowRight size={10} className="text-gray-300" />
        <FlowDot color={TAB_COLORS[1]} />
        <span>02</span>
        <ArrowRight size={10} className="text-gray-300" />
        <FlowDot color={TAB_COLORS[2]} />
        <span>03</span>
        <ArrowRight size={10} className="text-gray-300" />
        <FlowDot color={TAB_COLORS[3]} />
        <span>04</span>
        <ArrowRight size={10} className="text-gray-300" />
        <FlowDot color={TAB_COLORS[4]} />
        <span>05</span>
      </div>
      <p className="text-center text-[11px] text-gray-400 mt-2 font-medium">
        Align every layer. Reward every behavior. Measure every outcome.
      </p>
    </div>
  );
}
